import React, { FC, useCallback, useState } from "react";
import { Dialog, ButtonProps, Input } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface FacebookCapiConnectionProps extends IntegrationConnectionProps {
    integration: "facebook_capi";
    pixelId: string;
    accessToken: string;
    testEventCode?: string;
}

export interface FacebookCapiIntegrationCardProps extends IntegrationCardProp {
    pixelId?: string;
    testEventCode?: string;
    /**
     * Connect the Facebook Conversions API integration.
     */
    onConnect?(props: FacebookCapiConnectionProps): void;
}

// The Meta Pixel ID is a numeric string, typically 15-16 digits. Accept 10-20.
const isValidPixelId = (pixelId: string): boolean => /^\d{10,20}$/.test(pixelId.trim());

export const FacebookCapiIntegrationCard: FC<FacebookCapiIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    const [pixelId, setPixelId] = useState<string>(props.pixelId || "");
    const [accessToken, setAccessToken] = useState<string>("");
    const [testEventCode, setTestEventCode] = useState<string>(props.testEventCode || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            setShowDialog(true);
        }
    };

    const handleConnect = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "facebook_capi",
                pixelId: pixelId.trim(),
                accessToken: accessToken.trim(),
                testEventCode: testEventCode.trim() || undefined,
            });
        }
        setShowDialog(false);
    }, [onConnect, pixelId, accessToken, testEventCode]);

    const handleDisconnect = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !isValidPixelId(pixelId) || !accessToken.trim(),
            onClick: handleConnect,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/meta.png"
                integrationId="facebook_capi"
                text="Facebook Conversions API"
                href={() => "/integrations/facebook-capi"}
                onClick={handleClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnect : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="Facebook Conversions API Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>
                            Send lead conversions to Meta server-side, so they aren't lost to ad blockers or browser
                            tracking limits.
                        </p>
                        <p>
                            Events are deduplicated against your Meta Pixel, so this works alongside the pixel without
                            double-counting.
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Meta Pixel ID"
                            value={pixelId}
                            onChange={(value) => setPixelId(String(value))}
                            placeholder="e.g. 4092252157584843"
                        />
                        {pixelId && !isValidPixelId(pixelId) && (
                            <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                The Pixel ID must be a 10-20 digit number.
                            </p>
                        )}
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Access Token"
                            value={accessToken}
                            type="password"
                            onChange={(value) => setAccessToken(String(value))}
                            placeholder="Meta System User access token"
                        />
                        <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            Generate a System User token in Meta Events Manager with permission to send events to this
                            pixel.
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Test Event Code (Optional)"
                            value={testEventCode}
                            onChange={(value) => setTestEventCode(String(value))}
                            placeholder="e.g. TEST12345"
                        />
                        <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            While set, events route to the Test Events view in Events Manager instead of production.
                        </p>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
